import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import SettingsBackupRestore from '@material-ui/icons/SettingsBackupRestore';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import Card from '../../../components/Card/CardTS';
import CardBody from '../../../components/Card/CardBody.js';
import CardHeader from '../../../components/Card/CardHeader.js';
import CardIcon from '../../../components/Card/CardIcon.js';
import Snackbar from '../../../components/Snackbar/Snackbar';
import regularFormsStyle from '../../../assets/jss/material-dashboard-pro-react/views/regularFormsStyleTS';
import ResetSensorErrorCountsConfirmation from './ResetSensorErrorCountsConfirmation';
import { resetSensorErrorCounts } from '../../stores/actions/installerActions';

interface IResetSensorErrorCountsCardProps {
    classes: ClassNameMap;
    resetSensorErrorCounts: (systemId: number | null) => Promise<number | null>;
}

const ResetSensorErrorCountsCard = ({ classes, resetSensorErrorCounts }: IResetSensorErrorCountsCardProps) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [notification, setNotification] = useState<{ message: string; color: 'success' | 'danger' } | null>(null);

    useEffect(() => {
        if (!notification) return;
        const timeout = setTimeout(() => setNotification(null), 6000);
        return () => clearTimeout(timeout);
    }, [notification]);

    const reset = () => {
        setResetting(true);
        resetSensorErrorCounts(null).then((count) => {
            setResetting(false);
            if (count === null) {
                setNotification({ message: 'Resetting sensor error counters failed', color: 'danger' });
            } else {
                setConfirmOpen(false);
                setNotification({ message: 'Sensor error counters reset', color: 'success' });
            }
        });
    };

    return (
        <>
            {confirmOpen && (
                <ResetSensorErrorCountsConfirmation
                    message={<>The sensor error counters of all devices of all systems will be set to 0.<br />Are you sure you want to reset them?</>}
                    resetting={resetting}
                    onCancel={() => setConfirmOpen(false)}
                    onReset={reset}
                />
            )}
            <Card>
                <CardHeader color='info' icon>
                    <CardIcon color='info'>
                        <SettingsBackupRestore />
                    </CardIcon>
                    <h4 className={classes.cardIconTitle}>Sensor error counters</h4>
                </CardHeader>
                <CardBody>
                    <p>
                        Every device counts how many times a sensor error appeared. Resetting sets the counters of all devices
                        of all systems to 0 and they start counting again from now.
                    </p>
                    <Button size='medium' style={{ marginTop: '25px', transition: 'color 0.3s' }} onClick={() => setConfirmOpen(true)}>
                        Reset
                    </Button>
                </CardBody>
            </Card>
            <Snackbar
                close
                closeNotification={() => setNotification(null)}
                color={notification?.color ?? 'success'}
                message={notification?.message ?? ''}
                open={!!notification}
                place='tc'
            />
        </>
    );
};

export default connect(null, { resetSensorErrorCounts })(regularFormsStyle(ResetSensorErrorCountsCard));
